import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from "react-redux";
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { api, INTEGRATION_DEFINITIONS } from 'common';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import Button from "components/CustomButtons/Button.js";
import { MAIN_COLOR, SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";

const useStyles = makeStyles((theme) => ({
  typography: { fontFamily: FONT_FAMILY },
  form: {
    width: '60%',
    marginTop: theme.spacing(1),
    backgroundColor: MAIN_COLOR,
    alignContent: 'center',
    borderRadius: '8px',
  },
  pageTitle: {
    color: colors.WHITE,
    paddingTop: '15px',
    paddingLeft: '15px',
    paddingBottom: '15px',
    fontSize: '20px',
    fontFamily: FONT_FAMILY,
  },
  container1: {
    backgroundColor: colors.WHITE,
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    padding: '24px',
    width: '100%',
    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
  },
  apiCard: {
    border: `1px solid ${SECONDORY_COLOR}40`,
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '16px',
    backgroundColor: '#fafafa',
  },
  apiCardDisabled: {
    opacity: 0.55,
  },
  apiHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  apiName: {
    fontFamily: FONT_FAMILY,
    fontWeight: 600,
    fontSize: '15px',
    color: '#333',
  },
  apiSubtitle: {
    fontFamily: FONT_FAMILY,
    fontSize: '12px',
    color: '#888',
    marginTop: '2px',
  },
  noTokenChip: {
    display: 'inline-block',
    fontSize: '11px',
    fontFamily: FONT_FAMILY,
    backgroundColor: '#e8f5e9',
    color: '#388e3c',
    borderRadius: '4px',
    padding: '2px 8px',
    marginTop: '4px',
  },
  sectionDivider: { margin: '14px 0' },
  limitRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '4px',
  },
  limitTypeLabel: {
    fontFamily: FONT_FAMILY,
    fontSize: '13px',
    color: '#555',
  },
  limitField: {
    width: '160px',
    '& label.Mui-focused': { color: MAIN_COLOR },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: MAIN_COLOR },
    },
    '& input': { fontFamily: FONT_FAMILY },
  },
  radioLabel: { fontFamily: FONT_FAMILY, fontSize: '13px' },
  buttonStyle: {
    margin: 0,
    width: '100%',
    padding: '12px 24px',
    minWidth: '150px',
    height: 40,
    borderRadius: '30px',
    backgroundColor: MAIN_COLOR,
    color: colors.WHITE,
    fontFamily: FONT_FAMILY,
  },
  textField: {
    '& label.Mui-focused': { color: MAIN_COLOR },
    '& .MuiInput-underline:after': { borderBottomColor: MAIN_COLOR },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: MAIN_COLOR },
    },
    '& input': { fontFamily: FONT_FAMILY },
  },
  rootRtl: {
    '& label': { right: 10, left: 'auto', paddingRight: 20, fontFamily: FONT_FAMILY },
    '& legend': { textAlign: 'right', marginRight: 20, fontFamily: FONT_FAMILY },
    '& label.Mui-focused': { color: MAIN_COLOR },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: MAIN_COLOR },
    },
    '& input': { fontFamily: FONT_FAMILY },
  },
}));

// ---------------------------------------------------------------------------
// Valor padrão por API
// ---------------------------------------------------------------------------
const defaultApi = () => ({
  enabled: false,
  token: '',
  query_limit_enabled: false,
  limit_type: 'global',
  query_limit: '',
});

// ---------------------------------------------------------------------------
// Sub-componente: card de uma API
// ---------------------------------------------------------------------------
const ApiCard = ({ apiKey, def, section, onChange, isRTL, allowEdit, hiddenLabel, classes }) => {
  const { t } = useTranslation();

  const set = (field, value) => onChange(apiKey, field, value);

  return (
    <Box className={`${classes.apiCard} ${!section.enabled ? classes.apiCardDisabled : ''}`}>
      {/* cabeçalho */}
      <Box className={classes.apiHeader}>
        <Box>
          <Typography className={classes.apiName}>{t(`${apiKey}_api_label`)}</Typography>
          <Typography className={classes.apiSubtitle}>{t(`${apiKey}_api_sublabel`)}</Typography>
          {!def.requiresToken && (
            <span className={classes.noTokenChip}>{t('no_token_required')}</span>
          )}
        </Box>
        <FormControlLabel
          style={{ margin: 0 }}
          labelPlacement={isRTL === 'rtl' ? 'start' : 'end'}
          label={
            <Typography className={classes.typography} style={{ fontSize: '13px' }}>
              {section.enabled ? t('active') : t('inactive')}
            </Typography>
          }
          control={
            <Switch
              checked={section.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
              color="primary"
              size="small"
            />
          }
        />
      </Box>

      {/* corpo — só visível quando ativo */}
      <Collapse in={section.enabled} timeout="auto" unmountOnExit>
        {/* token — só para APIs que precisam */}
        {def.requiresToken && (
          <TextField
            InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
            className={isRTL === 'rtl' ? classes.rootRtl : classes.textField}
            variant="outlined"
            margin="normal"
            fullWidth
            label={t('api_token')}
            type="password"
            value={allowEdit ? (section.token || '') : hiddenLabel}
            onChange={(e) => set('token', e.target.value)}
          />
        )}

        <Divider className={classes.sectionDivider} />

        {/* switch de limite */}
        <FormControlLabel
          labelPlacement={isRTL === 'rtl' ? 'start' : 'end'}
          label={
            <Typography className={classes.typography} style={{ fontSize: '13px' }}>
              {t('query_limit_enabled')}
            </Typography>
          }
          control={
            <Switch
              checked={section.query_limit_enabled}
              onChange={(e) => set('query_limit_enabled', e.target.checked)}
              color="primary"
              size="small"
            />
          }
        />

        <Collapse in={section.query_limit_enabled} timeout="auto" unmountOnExit>
          <Box className={classes.limitRow} style={{ marginTop: '12px' }}>
            {/* tipo de limite */}
            <FormControl>
              <Typography className={classes.limitTypeLabel}>{t('limit_type')}</Typography>
              <RadioGroup
                row
                value={section.limit_type || 'global'}
                onChange={(e) => set('limit_type', e.target.value)}
              >
                <FormControlLabel
                  value="global"
                  control={<Radio size="small" color="primary" />}
                  label={<Typography className={classes.radioLabel}>{t('limit_type_global')}</Typography>}
                />
                <FormControlLabel
                  value="per_user"
                  control={<Radio size="small" color="primary" />}
                  label={<Typography className={classes.radioLabel}>{t('limit_type_per_user')}</Typography>}
                />
              </RadioGroup>
            </FormControl>

            {/* valor do limite */}
            <TextField
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              className={classes.limitField}
              variant="outlined"
              size="small"
              label={t('query_limit')}
              type="number"
              inputProps={{ min: 1 }}
              value={section.query_limit || ''}
              onChange={(e) =>
                set('query_limit', e.target.value === '' ? '' : parseInt(e.target.value, 10))
              }
            />
          </Box>
        </Collapse>
      </Collapse>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const IntegrationSettings = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { editIntegrationConfig } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const integrationDetails = useSelector((state) => state.integrationdata.integrationDetails);
  const classes = useStyles();
  const dispatch = useDispatch();
  const allowEdit = !!settings?.AllowCriticalEditsAdmin;

  const [data, setData] = useState({
    cpf:    defaultApi(),
    plates: defaultApi(),
    cep:    defaultApi(),
  });

  useEffect(() => {
    if (integrationDetails) {
      setData({
        cpf:    { ...defaultApi(), ...(integrationDetails.cpf    || {}) },
        plates: { ...defaultApi(), ...(integrationDetails.plates || {}) },
        cep:    { ...defaultApi(), ...(integrationDetails.cep    || {}) },
      });
    }
  }, [integrationDetails]);

  const handleFieldChange = (apiKey, field, value) => {
    setData((prev) => ({
      ...prev,
      [apiKey]: { ...prev[apiKey], [field]: value },
    }));
  };

  const handleSubmit = () => {
    if (allowEdit) {
      dispatch(editIntegrationConfig(data));
      alert(t('updated'));
    } else {
      alert(t('demo_mode'));
    }
  };

  const sharedProps = {
    onChange: handleFieldChange,
    isRTL,
    allowEdit,
    hiddenLabel: t('hidden_demo'),
    classes,
  };

  return (
    <form className={classes.form}>
      <Grid item xs={12}>
        <Typography component="h1" variant="h5" className={classes.pageTitle}
          style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
          {t('integrations_title')}
        </Typography>
      </Grid>

      <div className={classes.container1}>
        <Grid container style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
          <Grid item xs={12}>

            <ApiCard
              apiKey="cpf"
              def={INTEGRATION_DEFINITIONS.cpf}
              section={data.cpf}
              {...sharedProps}
            />

            <ApiCard
              apiKey="plates"
              def={INTEGRATION_DEFINITIONS.plates}
              section={data.plates}
              {...sharedProps}
            />

            <ApiCard
              apiKey="cep"
              def={INTEGRATION_DEFINITIONS.cep}
              section={data.cep}
              {...sharedProps}
            />

            <Button
              size="lg"
              onClick={handleSubmit}
              variant="contained"
              color="secondaryButton"
              className={classes.buttonStyle}
            >
              <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '14px' }}>
                {t('submit')}
              </Typography>
            </Button>

          </Grid>
        </Grid>
      </div>
    </form>
  );
};

export default IntegrationSettings;
